import { axisLeft, select } from 'd3';
import { ScaleLinear } from 'd3-scale';
import { useDripsyTheme } from 'dripsy';
import { useEffect, useRef } from 'react';
import { useStore } from 'store/store';
import { BarChartMargin } from '../barChart/type';

interface AxisYProps {
  width: number;
  height: number;
  yScale: ScaleLinear<number, number, never>;
  values: Array<number>;
  margin?: BarChartMargin;
  yTitle?: string;
}

export const AxisY: React.FC<AxisYProps> = ({
  width,
  height,
  yScale,
  values,
  margin = {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0
  },
  yTitle
}) => {
  const ref = useRef<SVGGElement>(null);
  const transform = `translate(${margin.left * 2}, -${height})`;

  const store = useStore();
  const textColor = store.theme === 'light' ? '#000000' : '#FFFFFF';
  const lineColor = store.theme === 'light' ? '#DCDCDC' : '#4A4A4A';

  const { theme } = useDripsyTheme();
  const fontFamily = theme.fonts?.body;

  useEffect(() => {
    if (ref.current) {
      const axisY = axisLeft(yScale);
      axisY.ticks(4).tickSize(-width);
      select(ref.current).call(axisY);

      select(ref.current).select('path').attr('stroke', 'transparent');
      // Ticks grey coloured
      select(ref.current).selectAll('line').attr('stroke', lineColor);
      // Ticks text, padding right 16px
      select(ref.current)
        .selectAll('text')
        .attr('font-size', '13px')
        .attr('font-family', 'Poppins_400Regular')
        .attr('x', -16)
        .attr('fill', textColor);

      // Add Axis Y label
      if (yTitle) {
        select(ref.current)
          .append('text')
          .attr('x', -height / 2)
          .attr('y', -margin.left + 10)
          .attr('transform', `rotate(-90) translate(${0}, -${margin.left})`)
          .attr('text-anchor', 'middle')
          .attr('font-size', '13px')
          .attr('font-family', 'Poppins_400Regular')
          .attr('fill', '#666')
          .text(yTitle);
      }
    }

    return () => {
      select(ref.current).selectAll('*').remove();
    };
  }, [values, yScale]);

  return <g ref={ref} transform={transform} />;
};
